import type {
    Signal,
    SignalFilters,
    SignalSource,
    SignalType,
} from "app/pages/sales-intelligence/domain/signals/types/signal";
import type SignalLifecycleStatus from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalLifecycleStatus";
import type SignalCreateDTO from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalCreateDTO";
import type SignalLifecycleState from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalLifecycleState";
import type SignalUpdateDTO from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalUpdateDTO";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";
import type { Translatable } from "app/pages/sales-intelligence/types";

import type { SignalTimeframeConfig } from "../miscellaneous/SignalTimeframe";

export type SignalTemplateDeps<Filters extends SignalFilters> = {
    readonly alias: string;
    readonly description: Readonly<Translatable>;
    readonly filters: Readonly<Partial<Filters>>;
    readonly name: Readonly<Translatable>;
    readonly signalSubType?: string;
    readonly timeframeConfig?: Readonly<SignalTimeframeConfig<Filters>>;
    readonly lifecycleStatus?: SignalLifecycleStatus;
    readonly signalLifecycleState?: SignalLifecycleState;
    readonly templateGroup?: number;
    readonly templateHint?: Readonly<Translatable>;
    readonly lastUpdatedDate?: Date;
} & Pick<Signal, "signalType" | "signalSource">;

abstract class SignalTemplate<Filters extends SignalFilters> {
    readonly alias: string;
    readonly description: Readonly<Translatable>;
    readonly filters: Readonly<Partial<Filters>>;
    readonly name: Readonly<Translatable>;
    readonly signalSubType?: string;
    readonly signalSource: SignalSource;
    readonly signalType: SignalType;
    readonly lifecycleStatus: SignalLifecycleStatus;
    readonly signalLifecycleState?: SignalLifecycleState;
    readonly timeframeConfig?: Readonly<SignalTimeframeConfig<Filters>>;
    readonly templateGroup?: number;
    readonly templateHint?: Readonly<Translatable>;
    readonly lastUpdatedDate?: Date;

    constructor(deps: SignalTemplateDeps<Filters>) {
        this.alias = deps.alias;
        this.description = deps.description;
        this.filters = deps.filters;
        this.name = deps.name;
        this.signalSubType = deps.signalSubType;
        this.signalSource = deps.signalSource;
        this.signalType = deps.signalType;
        this.timeframeConfig = deps.timeframeConfig;
        this.lifecycleStatus = deps.lifecycleStatus ?? "permanent";
        this.signalLifecycleState = deps.signalLifecycleState;
        this.templateGroup = deps.templateGroup;
        this.templateHint = deps.templateHint;
        this.lastUpdatedDate = deps.lastUpdatedDate;
    }

    abstract matchesSignal(signal: Readonly<Signal<Filters>>): boolean;

    abstract isValid(): boolean;

    abstract getSignalCreateDTO<F extends SignalFilters>(
        filters: Readonly<F>,
        signalSource: SignalSource,
    ): Readonly<SignalCreateDTO<F>>;

    abstract getSignalUpdateDTO<F extends SignalFilters>(
        uiSignal: Readonly<UISignal<F>>,
        filters: Readonly<F>,
    ): Readonly<SignalUpdateDTO<F>>;
}

export default SignalTemplate;
